// EST-FEAT-05/06 — Estimate generation + read (Phase 2 slice 2d), migrated to the Estimate-Set
// schema (`proposal structure and schema.md`). Backend: api-service
// app/api/v1/endpoints/estimates.py, app/domain/estimates/models.py.
//
// An `EstimateSet` groups every option offered for one engagement (Good/Better/Best, alternates —
// `EstimateOption`, keyed by `option_id`); each `Estimate` is one VERSION of one option, with a
// single unified `estimate_lines[]` array (one shape for both material and service lines,
// discriminated by `line_type`) instead of the old separate material_lines/boq_lines split.
//
// Line-level edits (override quantity/price, swap material, set commercial terms, recalculate)
// wrap the SAME EstimateService methods app.domain.estimates.agent_tools already exposes to
// SAM — no new business logic, just a REST path onto it so the web UI has the same access.
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';
import { workflowEngineApi } from '@/redux/internal-services/workflowEngineApi';
import { proposalsApi } from '@/redux/internal-services/proposalsApi';

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}
interface ApiListResponse<T> extends ApiResponse<T[]> {
  total: number;
}

// Platform reference data (app/domain/estimation_masters/models.py::WbsTemplate) — the costing
// package an estimate option is built against. Field names track the backend's own vocabulary,
// which the target data model renamed: wbs_code -> template_code, wbs_name -> template_name,
// package_type -> template_type, default_system_code -> roof_system. `GET
// /estimation-masters/wbs-templates` has returned the new names since that migration, so the
// old ones here read back `undefined`.
export interface WbsTemplate {
  template_code: string;
  template_name: string;
  template_type?: string | null;
  roof_system?: string | null;
  description?: string | null;
  version: string;
  assessment_template_code?: string | null;
  assessment_template_version?: string | null;
  active?: boolean;
}

// One row of a `GET /{engagement_id}/estimates` listing — an option's stable identity plus its
// CURRENT version's status/total, so the option tabs (Good/Better/Best) render without a second
// round trip per option. `option_id` is the tab key everywhere below (kept the old param name
// `estimateCode` on query hooks to minimize churn — it now carries an `option_id` value).
export interface EstimateOptionSummary {
  estimate_set_id: string;
  engagement_id: string;
  option_id: string;
  option_number: number;
  estimate_name: string;
  estimate_description?: string | null;
  estimation_method: 'DETAILED_COST_BASED';
  current_version: number;
  status: 'DRAFT' | 'COMPLETED' | 'LOCKED' | 'SUPERSEDED' | 'CANCELLED';
  grand_total?: number | null;
  currency: string;
  selected_for_proposal: boolean;
  // Set-level review state (owning EstimateSet.edit_state) and this current version's own
  // reviewed flag — denormalized here the same way status/grand_total already are. FROZEN =
  // reviewed (set on entry into proposal_prepared); REWORK = a revision was requested
  // (return_proposal). Editing a COMPLETED option while FROZEN/REWORK transparently forks a new
  // version server-side — see EstimateService._ensure_editable_version.
  edit_state: 'OPEN' | 'FROZEN' | 'REWORK';
  reviewed: boolean;
  created_at: string;
}

// The stable identity half of `EstimateVersionDetail` — an option's name/description, unchanged
// across its versions (only `current_version` moves as it's recalculated/re-priced).
// Mirrors app/domain/estimates/models.py's GenerationMethod — how this option picks its
// materials. Drives pricing via tenant_preferences.resolution.select_item.
export interface GenerationMethod {
  method_code: 'PREFERRED_SUPPLIER' | 'LOWEST_COST' | 'PREFERRED_MANUFACTURER' | 'SPECIFIC_PRODUCT' | 'MANUAL';
  parameters: {
    supplier_code?: string | null;
    manufacturer_code?: string | null;
    product_code?: string | null;
    product_variant_code?: string | null;
  };
}

export interface EstimateOption {
  option_id: string;
  option_number: number;
  estimate_name: string;
  estimate_description?: string | null;
  generation_method?: GenerationMethod;
  current_version: number;
  display_order: number;
  active: boolean;
}

export interface ValueOverride {
  overridden: boolean;
  override_value?: number | null;
  override_reason?: string | null;
}
export interface Wastage {
  method: 'NONE' | 'PERCENTAGE' | 'FIXED_QUANTITY';
  percent?: number | null;
  quantity?: number | null;
}

// Material costing for one `estimate_lines[]` entry (`line_type === 'MATERIAL'`) — every field
// the old flat `EstimateMaterialLine` had, renamed/re-nested to the schema's vocabulary.
export interface MaterialLineDetail {
  material_code: string;
  product_code?: string | null; // was item_code
  product_variant_code?: string | null;
  supplier_code?: string | null;
  supplier_product_code?: string | null;
  // Mirrors SelectionMethodUsed in app/domain/estimates/models.py — the union was missing three
  // of its members, so a swapped or catalogue-added line ('MANUAL') had no type for its own value.
  selection_method_used?: 'PREFERRED_SUPPLIER' | 'LOWEST_COST' | 'PREFERRED_MANUFACTURER'
  | 'SPECIFIC_PRODUCT' | 'MANUAL' | 'USER_SELECTED' | 'BENCHMARK';
  /** User id, set whenever a person chose this line's product or supplier by hand. It is what
   *  separates "the engine fell back" from "someone changed it on purpose". */
  selection_overridden_by?: string | null;
  required_quantity?: number | null; // was calculated_quantity — the driving qty before waste
  base_uom?: string | null;
  wastage?: Wastage;
  // Human percentage (15 = 15%) lives at `wastage.percent`, but as a FRACTION here (0.15) — same
  // engine convention the backend already used; override_material_line's `waste_percent` param
  // is the one place a human percentage (15) is expected.
  wastage_source?: 'PLATFORM_CATEGORY' | 'TENANT_OVERRIDE' | 'TEMPLATE' | 'WBS_RESOURCE' | 'USER_OVERRIDE' | 'NONE';
  effective_required_quantity?: number | null; // was required_quantity — post-waste qty
  purchase_uom?: string | null;
  purchase_quantity?: number | null;
  unit_cost?: number | null; // was unit_price
  price_uom?: string | null;
  price_source?: 'BENCHMARK' | 'TENANT_SUPPLIER' | 'MANUAL';
  effective_unit_cost?: number | null;
  currency?: string;
  extended_cost?: number | null; // was line_cost
}

// Service/BOQ costing for one `estimate_lines[]` entry (`line_type === 'SERVICE'`) — every field
// the old flat `EstimateBoqLine` had.
export interface ServiceLineDetail {
  service_code: string; // was boq_item_code / area_rate_code
  cost_category?: string | null;
  calculation_method?: 'RATE_BASED' | 'FIXED_AMOUNT' | 'ALLOWANCE' | 'PERCENTAGE_OF_BASE';
  required_quantity?: number | null; // was calculated_quantity
  uom?: string | null;
  effective_required_quantity?: number | null; // was rated_quantity
  base_rate?: number | null;
  rate_source?: string;
  effective_base_rate?: number | null;
  effective_combined_adjustment_percent?: number; // was combined_adjustment_percent
  effective_rate?: number | null;
  extended_cost?: number | null; // was line_amount
}

// One unified line — `line_type` discriminates which of `material`/`service` is populated (the
// other is null), replacing the old two-collection material_lines/boq_lines split.
export interface EstimateLine {
  estimate_line_id: string; // was estimate_material_line_code / estimate_boq_line_code
  estimate_version_code: string;
  engagement_id: string;
  activity_code?: string | null;
  resource_code?: string | null;
  line_type: 'MATERIAL' | 'SERVICE';
  /** What the line COSTS — M- material, L- labour, E- equipment, S- subcontract, C- consumable,
   *  P- permit. `line_type` only says which detail object is populated, so the editor groups on
   *  this. Set by the engine, so an unresolved line still groups correctly. */
  cost_type?: 'M-' | 'L-' | 'E-' | 'S-' | 'C-' | 'P-' | null;
  description?: string | null;
  material?: MaterialLineDetail | null;
  service?: ServiceLineDetail | null;
  quantity_status: 'CALCULATED' | 'QUANTITY_NOT_CALCULABLE';
  quantity_not_calculable_reason?: string | null;
  taxable: boolean;
}

// One version of one option — `estimates` collection document. Replaces the old
// `EstimateMaster`+`EstimateVersion` split; `estimate_id` is what every "…VersionCode" field
// below carries.
export interface Estimate {
  estimate_id: string; // was estimate_version_code
  estimate_number: string;
  estimate_set_id: string;
  option_id: string; // was estimate_code
  estimate_option_version: number; // was version_no
  status: 'DRAFT' | 'COMPLETED' | 'LOCKED' | 'SUPERSEDED' | 'CANCELLED';
  currency: string;
  engagement_id: string;
  estimation_method: 'DETAILED_COST_BASED';
  price_basis_date?: string | null;
  // EST-FR-06-002: whether this is the version a proposal will be priced from. Exactly one per
  // engagement carries it — the choice spans competing estimate options, not versions of one.
  selected_for_proposal?: boolean;
  locked_reason?: string | null;
  // Set when the engagement's estimate_set is marked reviewed (workflow entry into
  // proposal_prepared). A forked version never carries this forward, so a version created after
  // a revision request or an edit-while-frozen correctly starts unreviewed again.
  reviewed?: boolean;
  reviewed_at?: string | null;
  estimate_lines: EstimateLine[];
  created_by?: string | null;
  created_at: string;
}

// A step's (method, percent, amount) — all three optional since a step with no matching
// `pricing_adjustments[]` entry (nothing has been priced yet) comes back as `{}`.
export interface EstimateCommercialSummaryComponent {
  method?: 'PERCENTAGE' | 'FIXED_AMOUNT';
  percent?: number;
  amount?: number;
}

// Frontend-facing flattening of Estimate.cost_summary/pricing_adjustments/estimate_summary into
// the same shape the UI/PDF already render (material/work-cost/margin/discount/tax rows).
export interface EstimateCommercialSummary {
  estimate_version_code: string;
  engagement_id: string;
  material_cost_total: number;
  labour_cost_total: number;
  equipment_cost_total: number;
  subcontract_cost_total: number;
  permit_cost_total: number;
  boq_cost_total: number;
  direct_cost_total: number;
  contingency: EstimateCommercialSummaryComponent;
  overhead: EstimateCommercialSummaryComponent;
  margin: EstimateCommercialSummaryComponent;
  discount: EstimateCommercialSummaryComponent;
  /** Pre-tax throughout. The estimate's final figure is before proposal-level tax, which is
   *  applied when the proposal's Pricing & Options section is built. */
  subtotal: number;
  grand_total: number;
  currency: string;
}

// Composed read shape — an option's identity (`estimate`) + this version's lines/summary
// (`version`), so a single GET returns the whole estimate.
export interface EstimateVersionDetail {
  estimate: EstimateOption;
  version: Estimate;
  commercial_summary?: EstimateCommercialSummary | null;
}

// One row of the "Versions (N)" picker — a version plus its grand total, so the list can show
// what each one is worth without opening it. `grand_total`/`currency` are null for a DRAFT that
// hasn't been priced/recalculated yet.
export interface EstimateVersionSummary extends Estimate {
  grand_total?: number | null;
}

export interface EstimateValidationIssue {
  code: string;
  severity: 'BLOCKING' | 'WARNING';
  category: 'METHOD' | 'MEASUREMENT' | 'QUANTITY' | 'MATERIAL' | 'RATE' | 'PRICING' | 'COMMERCIAL';
  message: string;
  line_code?: string | null;
}
export interface EstimateValidationResult {
  estimate_version_code: string;
  can_complete: boolean;
  blocking: EstimateValidationIssue[];
  warnings: EstimateValidationIssue[];
}

export interface VariantAttribute {
  attribute_code: string;
  value: string | number | boolean | null;
  uom: string | null;
}

export interface MaterialItemDetail {
  product_code: string | null;
  product_name: string | null;
  product_line_code: string | null;
  manufacturer_code: string | null;
  material_code: string | null;
  variant_name: string | null;
  manufacturer_sku: string | null;
  purchase_uom: string | null;
  /** How many base units one purchase unit holds (33.33 SF per bundle, 5000 nails per box). */
  units_per_package: number | null;
  piy: number | null;
  coverage_uom: string | null;
  attributes: VariantAttribute[];
}
// Keyed by product_variant_code — MaterialLineDetail stores only the code, not the manufacturer
// or packaging text, so this is a separate lookup merged onto each line for display.
export type MaterialItemDetails = Record<string, MaterialItemDetail>;

/** Another variant of the SAME material — the swap candidates for a line. */
export interface AlternateVariant {
  product_variant_code: string;
  product_code: string;
  product_name: string | null;
  manufacturer_code: string | null;
  variant_name: string;
  purchase_uom: string;
  units_per_package: number | null;
}

export interface LineSupplier {
  supplier_code: string;
  supplier_name: string;
  supplier_product_code: string;
  price: number | null;
  price_uom: string | null;
  minimum_order_quantity: number | null;
  order_multiple: number | null;
  lead_time_days: number | null;
}

/** A supplier who carries at least one material ALREADY on this estimate — the option editor's
 *  "Supplier" chips. `line_count` of `total_items` says how far that supplier reaches; the rest of
 *  the lines keep their default price source. */
export interface VersionSupplier {
  supplier_code: string;
  supplier_name: string;
  line_count: number;
  total_items: number;
}

/** A manufacturer making a product for the materials on this estimate — the option editor's
 *  PREFERRED_MANUFACTURER chips. Deliberately wider than VersionSupplier: choosing a manufacturer
 *  CHANGES which product is specified, so the candidates come from the materials' whole catalogue
 *  rather than from the variants already chosen. */
export interface VersionManufacturer {
  manufacturer_code: string;
  /** Materials on this estimate that choosing them would actually change. */
  material_count: number;
  total_materials: number;
}

/** A row of the product-variant catalogue, as the "+ Add material" picker needs it. */
export interface CatalogueVariant {
  product_variant_code: string;
  variant_name: string;
  product_code: string;
  purchase_uom: string;
  units_per_package: number | null;
}

/** A row of the service catalogue, as the "+ Add service" picker needs it. */
export interface CatalogueService {
  service_code: string;
  service_name: string;
  service_type_code: string;
  service_category: string | null;
  default_uom: string;
}

export interface OverrideMaterialLineInput {
  estimate_material_line_code: string;
  calculated_quantity?: number;
  waste_percent?: number;
  /** Wastage as a flat quantity in the line's base UOM. Mutually exclusive with waste_percent. */
  waste_quantity?: number;
  unit_price?: number;
  override_reason: string;
}
export interface OverrideBoqLineInput {
  estimate_boq_line_code: string;
  rated_quantity?: number;
  base_rate?: number;
  override_reason: string;
}
export interface SwapMaterialLineVariantInput {
  estimate_material_line_code: string;
  new_product_variant_code: string;
}
export interface AddCustomMaterialLineInput {
  estimate_version_code: string;
  name: string;
  quantity: number;
  unit_price: number;
  uom?: string;
}
export interface AddCustomBoqLineInput {
  estimate_version_code: string;
  name: string;
  cost_category: 'LABOUR' | 'EQUIPMENT' | 'SUBCONTRACT' | 'DISPOSAL' | 'LOGISTICS' | 'PERMIT' | 'ALLOWANCE' | 'INDIRECT' | 'OTHER';
  quantity: number;
  rate: number;
  uom?: string;
}

export interface UpdateCommercialTermsInput {
  estimate_version_code: string;
  margin_percent?: number;
  contingency_percent?: number;
  overhead_percent?: number;
  discount_percent?: number;
  margin_amount?: number;
  contingency_amount?: number;
  overhead_amount?: number;
  discount_amount?: number;
}

export interface GenerateEstimateInput {
  engagement_id: string;
  // Optional — omit to let the backend resolve the tenant's default package for this engagement
  // type (estimation_wbs_defaults.engagement_type_wbs_map, via EstimateService.resolve_wbs_code).
  wbs_code?: string;
  estimate_option_label?: string;
  // How this option picks (and prices) its materials — "GAF via ABC Supply" vs "lowest cost" is
  // what makes two options in one set worth comparing, so it is set when the option is CREATED
  // rather than corrected afterwards. Backend-side this has always been accepted
  // (GenerateEstimateRequest.generation_method); omitting it falls through to the tenant's
  // standing preferences exactly as before.
  generation_method?: GenerationMethod;
  // Omit to use the tenant's configured preferred method (EST-CFG-TNT-05-001); name one to
  // request the other supported method for this estimate.
  estimation_method?: 'DETAILED_COST_BASED';
  margin_percent?: number;
  contingency_percent?: number;
  overhead_percent?: number;
  discount_percent?: number;
  // Flat currency alternatives. Where one is supplied it replaces its percentage for that step,
  // and the resulting component comes back with method 'FIXED_AMOUNT' and percent 0.
  margin_amount?: number;
  contingency_amount?: number;
  overhead_amount?: number;
  discount_amount?: number;
}

export const estimatesApi = createApi({
  reducerPath: 'estimatesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['EstimateList', 'Estimate'],
  endpoints: (builder) => ({
    listEstimatesForEngagement: builder.query<EstimateOptionSummary[], string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/estimates`,
      transformResponse: (res: ApiListResponse<EstimateOptionSummary>) => res.data,
      providesTags: ['EstimateList'],
    }),
    // `estimateCode` is an option_id — kept as the param name callers already use.
    getEstimate: builder.query<EstimateVersionDetail, { estimateCode: string; engagementId: string }>({
      query: ({ estimateCode, engagementId }) => ({
        url: `/api/v1/estimates/${estimateCode}`, params: { engagement_id: engagementId },
      }),
      transformResponse: (res: ApiResponse<EstimateVersionDetail>) => res.data,
      providesTags: (_r, _e, arg) => [{ type: 'Estimate' as const, id: arg.estimateCode }],
    }),
    generateEstimate: builder.mutation<EstimateVersionDetail, GenerateEstimateInput>({
      query: ({ engagement_id, ...body }) => ({
        url: `/api/v1/engagements/${engagement_id}/estimates`, method: 'POST', body,
      }),
      transformResponse: (res: ApiResponse<EstimateVersionDetail>) => res.data,
      invalidatesTags: ['EstimateList'],
      // The proposal's option list (useGetCurrentProposalQuery) is cached under proposalsApi's
      // `Proposal` tag — a separate API slice this mutation's own `invalidatesTags` can't reach.
      // Without this, a newly added option stayed missing from the proposal view until reload.
      onQueryStarted: async ({ engagement_id }, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(proposalsApi.util.invalidateTags([{ type: 'Proposal', id: engagement_id }]));
      },
    }),
    // The "Versions (N)" picker's row data — every version ever created for this option.
    listEstimateVersions: builder.query<EstimateVersionSummary[], { estimateCode: string; engagementId: string }>({
      query: ({ estimateCode, engagementId }) => ({
        url: `/api/v1/estimates/${estimateCode}/versions`, params: { engagement_id: engagementId },
      }),
      transformResponse: (res: ApiListResponse<EstimateVersionSummary>) => res.data,
      providesTags: (_r, _e, arg) => [{ type: 'Estimate' as const, id: arg.estimateCode }],
    }),
    // A specific past version, read-only — same shape getEstimate returns for the current one.
    getEstimateVersion: builder.query<EstimateVersionDetail, { estimateCode: string; engagementId: string; versionNo: number }>({
      query: ({ estimateCode, engagementId, versionNo }) => ({
        url: `/api/v1/estimates/${estimateCode}/versions/${versionNo}`, params: { engagement_id: engagementId },
      }),
      transformResponse: (res: ApiResponse<EstimateVersionDetail>) => res.data,
      providesTags: (_r, _e, arg) => [{ type: 'Estimate' as const, id: arg.estimateCode }],
    }),
    // Copy-on-write (§4.2): clones the current version into a new editable DRAFT, marking the
    // one it copied from SUPERSEDED — how a COMPLETED/LOCKED version becomes editable again.
    forkEstimateVersion: builder.mutation<EstimateVersionDetail, { estimateCode: string; engagementId: string }>({
      query: ({ estimateCode, engagementId }) => ({
        url: `/api/v1/estimates/${estimateCode}/fork`, method: 'POST', params: { engagement_id: engagementId },
      }),
      transformResponse: (res: ApiResponse<EstimateVersionDetail>) => res.data,
      invalidatesTags: ['Estimate', 'EstimateList'],
    }),
    // What's blocking (or just warning on) this version completing — EST-FR-05-011. Readable at
    // any time, not just when Complete is attempted, so a Sales Agent can see and fix issues
    // before hitting the blocked action.
    getEstimateValidation: builder.query<EstimateValidationResult, string>({
      query: (estimateVersionCode) => `/api/v1/estimates/versions/${estimateVersionCode}/validation`,
      transformResponse: (res: ApiResponse<EstimateValidationResult>) => res.data,
      providesTags: (_r, _e, versionCode) => [{ type: 'Estimate' as const, id: versionCode }],
    }),
    // EST-FR-06-002/003: marks a DRAFT version COMPLETED, making it eligible as a proposal's
    // pricing basis.
    completeEstimateVersion: builder.mutation<Estimate, string>({
      query: (estimateVersionCode) => ({
        url: `/api/v1/estimates/versions/${estimateVersionCode}/complete`, method: 'POST',
      }),
      transformResponse: (res: ApiResponse<Estimate>) => res.data,
      invalidatesTags: ['Estimate', 'EstimateList'],
      // Completing a version can flip the engagement's next-transition gate readiness (e.g. the
      // "Prepare Proposal" gate requires a completed estimate) — that gate state is cached under
      // workflowEngineApi's `AvailableTransitions` tag, a separate API slice whose tags this
      // mutation's own `invalidatesTags` can't reach. Without this, the gate-readiness banner
      // stayed stale until a full page reload. `estimateVersionCode` doesn't carry the
      // engagement_id here, so this invalidates the tag broadly (all engagements) rather than by
      // id — acceptable since only one engagement's transitions are ever shown at a time.
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(workflowEngineApi.util.invalidateTags(['AvailableTransitions']));
      },
    }),
    // EST-FR-06-002: which of an engagement's estimate OPTIONS (there can be several — Good/
    // Better/Best, alternates) is the one to quote. Requires the version be COMPLETED first.
    /** Soft-deletes one pricing option. Refused by the backend when it is the last option, when
     *  its version is locked to an approved proposal, or when it is the option the proposal
     *  currently recommends. */
    deleteEstimateOption: builder.mutation<void, { engagementId: string; optionId: string }>({
      query: ({ engagementId, optionId }) => ({
        url: `/api/v1/engagements/${engagementId}/estimates/options/${optionId}`,
        method: 'DELETE',
      }),
      // Both: the option disappears from the tab list AND the proposal it was on is rebuilt.
      invalidatesTags: ['EstimateList', 'Estimate'],
      // Same cross-slice gap as generateEstimate above — the proposal's option list is cached
      // under proposalsApi's `Proposal` tag, which this mutation's own tags can't reach.
      onQueryStarted: async ({ engagementId }, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(proposalsApi.util.invalidateTags([{ type: 'Proposal', id: engagementId }]));
      },
    }),
    selectEstimateForProposal: builder.mutation<Estimate, string>({
      query: (estimateVersionCode) => ({
        url: `/api/v1/estimates/versions/${estimateVersionCode}/select-for-proposal`, method: 'POST',
      }),
      transformResponse: (res: ApiResponse<Estimate>) => res.data,
      invalidatesTags: ['Estimate', 'EstimateList'],
    }),
    // Generic platform reference-data listing (app/api/v1/endpoints/estimation_masters.py) — used
    // here only for the "wbs-templates" collection, to populate the WBS-template picker when
    // adding a new estimate option. Read-only platform data, no tenant tag needed.
    listWbsTemplates: builder.query<WbsTemplate[], void>({
      query: () => '/api/v1/estimation-masters/wbs-templates',
      transformResponse: (res: ApiListResponse<WbsTemplate>) => res.data,
    }),
    // The pickers behind "+ Add material" / "+ Add service". Both collections are small (262
    // items, 85 BOQ rows) and are platform reference data that changes rarely, so they load once
    // and filter in the browser rather than needing a bespoke search endpoint per collection —
    // the generic masters listing above already serves them.
    listCatalogueVariants: builder.query<CatalogueVariant[], void>({
      query: () => '/api/v1/estimation-masters/product-variants',
      transformResponse: (res: ApiListResponse<CatalogueVariant>) => res.data,
    }),
    listCatalogueServices: builder.query<CatalogueService[], void>({
      query: () => '/api/v1/estimation-masters/services',
      transformResponse: (res: ApiListResponse<CatalogueService>) => res.data,
    }),
    addCatalogueMaterialLine: builder.mutation<EstimateLine, { estimate_version_code: string; product_variant_code: string; quantity: number; wastage_percent?: number }>({
      query: ({ estimate_version_code, ...body }) => ({
        url: `/api/v1/estimates/versions/${estimate_version_code}/lines/material/catalogue`,
        method: 'POST', body,
      }),
      transformResponse: (res: ApiResponse<EstimateLine>) => res.data,
      invalidatesTags: ['Estimate'],
    }),
    addCatalogueServiceLine: builder.mutation<EstimateLine, { estimate_version_code: string; service_code: string; quantity: number }>({
      query: ({ estimate_version_code, ...body }) => ({
        url: `/api/v1/estimates/versions/${estimate_version_code}/lines/service/catalogue`,
        method: 'POST', body,
      }),
      transformResponse: (res: ApiResponse<EstimateLine>) => res.data,
      invalidatesTags: ['Estimate'],
    }),
    overrideMaterialLine: builder.mutation<EstimateLine, OverrideMaterialLineInput>({
      query: ({ estimate_material_line_code, ...body }) => ({
        url: `/api/v1/estimates/lines/material/${estimate_material_line_code}`, method: 'PATCH', body,
      }),
      transformResponse: (res: ApiResponse<EstimateLine>) => res.data,
      // Both getEstimate (keyed by option_id) and getEstimateValidation (keyed by
      // estimate_version_code) tag as 'Estimate' with different ids — a whole-type invalidation
      // (no id) is the simplest way to refresh both cache shapes after any line edit.
      invalidatesTags: ['Estimate'],
    }),
    overrideBoqLine: builder.mutation<EstimateLine, OverrideBoqLineInput>({
      query: ({ estimate_boq_line_code, ...body }) => ({
        url: `/api/v1/estimates/lines/boq/${estimate_boq_line_code}`, method: 'PATCH', body,
      }),
      transformResponse: (res: ApiResponse<EstimateLine>) => res.data,
      invalidatesTags: ['Estimate'],
    }),
    swapMaterialLineVariant: builder.mutation<EstimateLine, SwapMaterialLineVariantInput>({
      query: ({ estimate_material_line_code, ...body }) => ({
        url: `/api/v1/estimates/lines/material/${estimate_material_line_code}/swap`, method: 'POST', body,
      }),
      transformResponse: (res: ApiResponse<EstimateLine>) => res.data,
      invalidatesTags: ['Estimate'],
    }),
    updateCommercialTerms: builder.mutation<EstimateCommercialSummary, UpdateCommercialTermsInput>({
      query: ({ estimate_version_code, ...body }) => ({
        url: `/api/v1/estimates/versions/${estimate_version_code}/commercial-terms`, method: 'PATCH', body,
      }),
      transformResponse: (res: ApiResponse<EstimateCommercialSummary>) => res.data,
      invalidatesTags: ['Estimate'],
    }),
    recalculateEstimate: builder.mutation<EstimateCommercialSummary, string>({
      query: (estimateVersionCode) => ({
        url: `/api/v1/estimates/versions/${estimateVersionCode}/recalculate`, method: 'POST',
      }),
      transformResponse: (res: ApiResponse<EstimateCommercialSummary>) => res.data,
      invalidatesTags: ['Estimate'],
    }),
    getAlternateVariants: builder.query<AlternateVariant[], string>({
      query: (estimateMaterialLineCode) => `/api/v1/estimates/lines/material/${estimateMaterialLineCode}/alternate-variants`,
      transformResponse: (res: ApiResponse<AlternateVariant[]>) => res.data,
      providesTags: (_r, _e, lineCode) => [{ type: 'Estimate' as const, id: lineCode }],
    }),
    getManufacturersForVersion: builder.query<VersionManufacturer[], string>({
      query: (estimateVersionCode) => `/api/v1/estimates/versions/${estimateVersionCode}/manufacturers`,
      transformResponse: (res: ApiResponse<VersionManufacturer[]>) => res.data,
      providesTags: (_r, _e, versionCode) => [{ type: 'Estimate' as const, id: versionCode }],
    }),
    getSuppliersForVersion: builder.query<VersionSupplier[], string>({
      query: (estimateVersionCode) => `/api/v1/estimates/versions/${estimateVersionCode}/suppliers`,
      transformResponse: (res: ApiResponse<VersionSupplier[]>) => res.data,
      providesTags: (_r, _e, versionCode) => [{ type: 'Estimate' as const, id: versionCode }],
    }),
    /** Re-price the option under a new materials basis. Distinct from `recalculateEstimate`,
     *  which only re-sums the stored lines and cannot change what is quoted. */
    repriceWithBasis: builder.mutation<unknown, { estimate_version_code: string; generation_method: GenerationMethod }>({
      query: ({ estimate_version_code, generation_method }) => ({
        url: `/api/v1/estimates/versions/${estimate_version_code}/reprice`,
        method: 'POST', body: generation_method,
      }),
      invalidatesTags: ['Estimate'],
    }),
    /** Price one line from a specific supplier, keeping its item. `supplier_code: null` returns
     *  the line to automatic resolution. */
    setLineSupplier: builder.mutation<unknown, { estimate_material_line_code: string; supplier_code: string | null }>({
      query: ({ estimate_material_line_code, supplier_code }) => ({
        url: `/api/v1/estimates/lines/material/${estimate_material_line_code}/supplier`,
        method: 'POST', body: { supplier_code },
      }),
      invalidatesTags: ['Estimate'],
    }),
    getSuppliersForLine: builder.query<LineSupplier[], string>({
      query: (estimateMaterialLineCode) => `/api/v1/estimates/lines/material/${estimateMaterialLineCode}/suppliers`,
      transformResponse: (res: ApiResponse<LineSupplier[]>) => res.data,
      providesTags: (_r, _e, lineCode) => [{ type: 'Estimate' as const, id: lineCode }],
    }),
    getMaterialItemDetails: builder.query<MaterialItemDetails, string>({
      query: (estimateVersionCode) => `/api/v1/estimates/versions/${estimateVersionCode}/item-details`,
      transformResponse: (res: ApiResponse<MaterialItemDetails>) => res.data,
      providesTags: (_r, _e, versionCode) => [{ type: 'Estimate' as const, id: versionCode }],
    }),
    addCustomMaterialLine: builder.mutation<EstimateLine, AddCustomMaterialLineInput>({
      query: ({ estimate_version_code, ...body }) => ({
        url: `/api/v1/estimates/versions/${estimate_version_code}/lines/material/custom`, method: 'POST', body,
      }),
      transformResponse: (res: ApiResponse<EstimateLine>) => res.data,
      invalidatesTags: ['Estimate'],
    }),
    addCustomBoqLine: builder.mutation<EstimateLine, AddCustomBoqLineInput>({
      query: ({ estimate_version_code, ...body }) => ({
        url: `/api/v1/estimates/versions/${estimate_version_code}/lines/boq/custom`, method: 'POST', body,
      }),
      transformResponse: (res: ApiResponse<EstimateLine>) => res.data,
      invalidatesTags: ['Estimate'],
    }),
  }),
});

export const {
  useListEstimatesForEngagementQuery,
  useGetEstimateQuery,
  useGenerateEstimateMutation,
  useListEstimateVersionsQuery,
  useGetEstimateVersionQuery,
  useForkEstimateVersionMutation,
  useGetEstimateValidationQuery,
  useCompleteEstimateVersionMutation,
  useSelectEstimateForProposalMutation,
  useDeleteEstimateOptionMutation,
  useListWbsTemplatesQuery,
  useOverrideMaterialLineMutation,
  useOverrideBoqLineMutation,
  useSwapMaterialLineVariantMutation,
  useUpdateCommercialTermsMutation,
  useRecalculateEstimateMutation,
  useAddCustomMaterialLineMutation,
  useAddCustomBoqLineMutation,
  useGetMaterialItemDetailsQuery,
  useGetAlternateVariantsQuery,
  useGetSuppliersForLineQuery,
  useGetSuppliersForVersionQuery,
  useGetManufacturersForVersionQuery,
  useListCatalogueVariantsQuery,
  useListCatalogueServicesQuery,
  useAddCatalogueMaterialLineMutation,
  useAddCatalogueServiceLineMutation,
  useSetLineSupplierMutation,
  useRepriceWithBasisMutation,
} = estimatesApi;
